import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Button, CircularProgress, IconButton, InputAdornment, Modal, Stack, TextField, Typography, useTheme } from "@mui/material";
import { listAllMokshaBookings, listAllHotels, resetAllMokshaBookings, resetAllHotelsList, createMokshaInvoice } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { tableStyle } from "../mui-styles/table";
import { PaginationFooter } from "../../components";
import { CalendarMonth, Close, Download } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { pdf } from "@react-pdf/renderer";
import CsvDownloader from "react-csv-downloader";
import Header from "../../components/Header";
import CommissionInvoicePDF from "./CommissionInvoicePDF";
import TCSInvoicePDF from "./TCSInvoicePDF";
import moment from "moment";

const MokshaInvoices = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const startInputRef = useRef();
    const endInputRef = useRef();
    const [hotelId, setHotelId] = useState("");
    const [startDate, setStartDate] = useState(0);
    const [endDate, setEndDate] = useState(0);
    const [confirmModal, setConfirmModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [editRackRate, setEditRackRate] = useState("");
    const [editSellRate, setEditSellRate] = useState("");

    const modalStyle = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { sm: 460, xs: "92%" },
        maxHeight: "90vh",
        overflowY: "auto",
        bgcolor: colors.primary[400],
        outline: "none",
        boxShadow: 24,
        borderRadius: 2,
        p: 2,
    };

    const handleDownloadClick = (row, type) => {
        const inv = row.moksha_invoice;
        setSelectedInvoice({ row, type });
        setEditRackRate(String(inv ? inv.rack_rate : getRackRate(row)));
        setEditSellRate(String(inv ? inv.sell_rate : getSellRate(row)));
        setConfirmModal(true);
    };

    const downloadPdf = async ({ row, type, invoice, calculations }) => {
        const guestName = `${row.title || ""} ${row.guest_name || ""}`.trim() || "Guest";
        const invoiceNumber = type === "commission"
            ? invoice.commission_invoice_number
            : invoice.tcs_invoice_number;
        const pdfRow = {
            ...row,
            moksha_rack_rate: invoice.rack_rate,
            custom_price: invoice.sell_rate,
            total_price: invoice.sell_rate,
        };
        const doc = type === "commission"
            ? <CommissionInvoicePDF row={pdfRow} calculations={calculations} invoiceNumber={invoiceNumber} />
            : <TCSInvoicePDF row={pdfRow} calculations={calculations} invoiceNumber={invoiceNumber} />;
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${invoiceNumber || (type === "commission" ? "Commission" : "TCS")}_${guestName.replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleConfirmDownload = async () => {
        if (!selectedInvoice) return;
        setDownloading(true);
        try {
            const { row, type } = selectedInvoice;
            let invoice = row.moksha_invoice;

            if (!invoice) {
                // First time: save the invoice (also updates the booking) then download
                const rackRate = parseFloat(editRackRate) || 0;
                const sellRate = parseFloat(editSellRate) || 0;
                const nights = getTotalNights(row.checkin_date, row.checkout_date);
                const calc = computeMoksha({
                    rackRate,
                    sellRate,
                    isGstRegistered: row.hotel_id?.gst_enabled !== false,
                    nights,
                });
                const saved = await dispatch(createMokshaInvoice({
                    bookingId: row._id,
                    invoiceDate: moment().unix(),
                    nights,
                    rackRate,
                    sellRate,
                    gstSlab: calc.gstSlab,
                    isGstRegistered: calc.isGstRegistered,
                    payableToProperty: calc.payableToProperty,
                    phsCommission: calc.phsCommission,
                    gst: calc.gst,
                    sgst: calc.gst / 2,
                    cgst: calc.gst / 2,
                    tcs: calc.tcs,
                    tds: calc.tds,
                    totalDeductions: calc.totalDeductions,
                    netPayable: calc.netPayable,
                }));
                if (!saved) {
                    setDownloading(false);
                    return;
                }
                invoice = saved;
                // Refresh table so saved invoice + updated booking values show
                dispatch(resetAllMokshaBookings());
                fetchMoreData();
            }

            await downloadPdf({ row, type, invoice, calculations: savedToCalc(invoice) });
        } catch (err) {
            console.error("PDF generation failed:", err);
        }
        setDownloading(false);
        setConfirmModal(false);
        setSelectedInvoice(null);
    };

    useEffect(() => {
        dispatch(resetAllHotelsList());
        dispatch(listAllHotels());
    }, [dispatch]);

    useEffect(() => {
        dispatch(resetAllMokshaBookings());
        fetchMoreData();
    // eslint-disable-next-line
    }, [hotelId, startDate, endDate]);

    const fetchMoreData = (page) => {
        dispatch(listAllMokshaBookings({
            hotelId: hotelId === "all" ? "" : hotelId,
            ...page,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
        }));
    };

    const { mokshaList, hotels, pageNumber, pageSize, count } = useSelector(
        ({
            admin: {
                mokshaBookings: { list, pageNumber, pageSize, count },
                hotels,
            },
        }) => ({
            mokshaList: list,
            pageNumber, pageSize, count,
            hotels,
        })
    );

    const optionsList = hotels?.list?.map((el) => ({
        label: el.name,
        id: el._id,
    }));

    const CustomFooter = () => (
        <PaginationFooter
            count={count}
            pageSize={pageSize}
            pageNumber={pageNumber}
            fetchMoreData={(page) => fetchMoreData(page)}
        />
    );

    const getTotalNights = (checkin, checkout) => {
        if (!checkin || !checkout) return 0;
        return moment.unix(checkout).diff(moment.unix(checkin), "days");
    };

    const getSellRate = (row) => row.custom_price > 0 ? row.custom_price : row.total_price;
    const getRackRate = (row) => row.moksha_rack_rate || 0;

    // Pure calculation from raw inputs
    const computeMoksha = ({ rackRate, sellRate, isGstRegistered, nights }) => {
        const n = nights || 1;
        const perNightRackRate = rackRate / n;
        const gstSlab = perNightRackRate >= 7500 ? 0.18 : 0.05;
        const payableToProperty = rackRate * 0.75;
        const paidByPHS = 0;
        const paidByProperty = 0;
        const sellRateExclGST = sellRate / (1 + gstSlab);
        const phsCommission = (sellRate - payableToProperty) / (1 + gstSlab);
        const gst = phsCommission * 0.18;
        const tcs = isGstRegistered ? sellRateExclGST * 0.01 : sellRateExclGST * gstSlab;
        const tds = sellRateExclGST * 0.02;
        const totalDeductions = phsCommission + gst + tcs + tds;
        const netPayable = sellRate - totalDeductions;
        return { payableToProperty, paidByPHS, paidByProperty, phsCommission, gst, tcs, totalDeductions, tds, netPayable, gstSlab, isGstRegistered, sellRateExclGST };
    };

    // Map a saved invoice snapshot to the calculations shape used by the table/PDFs
    const savedToCalc = (inv) => {
        const gstSlab = inv.gst_slab ?? 0.18;
        return {
            payableToProperty: inv.payable_to_property || 0,
            paidByPHS: 0,
            paidByProperty: 0,
            phsCommission: inv.phs_commission || 0,
            gst: inv.gst || 0,
            tcs: inv.tcs || 0,
            totalDeductions: inv.total_deductions || 0,
            tds: inv.tds || 0,
            netPayable: inv.net_payable || 0,
            gstSlab,
            isGstRegistered: inv.is_gst_registered !== false,
            sellRateExclGST: (inv.sell_rate || 0) / (1 + gstSlab),
        };
    };

    const calcColumns = (row) => {
        if (row.moksha_invoice) return savedToCalc(row.moksha_invoice);
        return computeMoksha({
            rackRate: getRackRate(row),
            sellRate: getSellRate(row),
            isGstRegistered: row.hotel_id?.gst_enabled !== false,
            nights: getTotalNights(row.checkin_date, row.checkout_date),
        });
    };

    const generateCsvDownloadData = (data) => {
        return data.map((row) => {
            const c = calcColumns(row);
            return {
                "Check-in": moment.unix(row.checkin_date).format("YYYY-MM-DD"),
                "Check-out": moment.unix(row.checkout_date).format("YYYY-MM-DD"),
                "Guest Name": `${row.title || ""} ${row.guest_name || ""}`.trim(),
                "Hotel": row.hotel_id?.name || "-",
                "Nights": getTotalNights(row.checkin_date, row.checkout_date),
                "Rack Rate": getRackRate(row),
                "Sell Rate": getSellRate(row),
                "Payable to Property": c.payableToProperty.toFixed(2),
                "Payment by PHS": "0.00",
                "Payment by Property": c.paidByProperty.toFixed(2),
                "PHS Commission": c.phsCommission.toFixed(2),
                "GST On Commission": c.gst.toFixed(2),
                "TCS": c.tcs.toFixed(2),
                "Total Deductions": c.totalDeductions.toFixed(2),
                "TDS": c.tds.toFixed(2),
                "Net Payable": c.netPayable.toFixed(2),
                "Status": row.status,
            };
        });
    };

    const columns = [
        {
            field: "checkin_date",
            headerName: "Check-in",
            width: 110,
            valueGetter: (params) => params.row.checkin_date ? moment.unix(params.row.checkin_date).format("YYYY-MM-DD") : "-",
        },
        {
            field: "checkout_date",
            headerName: "Check-out",
            width: 110,
            valueGetter: (params) => params.row.checkout_date ? moment.unix(params.row.checkout_date).format("YYYY-MM-DD") : "-",
        },
        {
            field: "guest_name",
            headerName: "Guest Name",
            width: 180,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography color={colors.greenAccent[400]}>
                        <b>{`${row.title || ""} ${row.guest_name || ""}`.trim() || "-"}</b>
                    </Typography>
                    <Typography fontSize="small">{row.guest_email || ""}</Typography>
                </Stack>
            ),
        },
        {
            field: "hotel_id",
            headerName: "Hotel",
            width: 150,
            renderCell: ({ row: { hotel_id } }) => hotel_id?.name ?? "-",
        },
        {
            field: "total_nights",
            headerName: "Nights",
            width: 70,
            renderCell: ({ row }) => getTotalNights(row.checkin_date, row.checkout_date),
        },
        {
            field: "moksha_rack_rate",
            headerName: "Rack Rate",
            width: 100,
            renderCell: ({ row }) => getRackRate(row) ? `₹${getRackRate(row)}` : "-",
        },
        {
            field: "total_price",
            headerName: "Sell Rate",
            width: 100,
            renderCell: ({ row }) => `₹${getSellRate(row)}`,
        },
        {
            field: "payable_to_property",
            headerName: "Payable to Property",
            width: 140,
            renderCell: ({ row }) => `₹${calcColumns(row).payableToProperty.toFixed(2)}`,
        },
        {
            field: "paid_by_phs",
            headerName: "Collected by PHS",
            width: 130,
            renderCell: () => "₹0.00",
        },
        {
            field: "paid_by_property",
            headerName: "Collected by Property",
            width: 140,
            renderCell: ({ row }) => `₹${calcColumns(row).paidByProperty.toFixed(2)}`,
        },
        {
            field: "phs_commission",
            headerName: "PHS Commission",
            width: 130,
            renderCell: ({ row }) => `₹${calcColumns(row).phsCommission.toFixed(2)}`,
        },
        {
            field: "gst",
            headerName: "GST On Commission",
            width: 150,
            renderCell: ({ row }) => `₹${calcColumns(row).gst.toFixed(2)}`,
        },
        {
            field: "tcs",
            headerName: "TCS",
            width: 90,
            renderCell: ({ row }) => `₹${calcColumns(row).tcs.toFixed(2)}`,
        },
        {
            field: "total_deductions",
            headerName: "Total Deductions",
            width: 110,
            renderCell: ({ row }) => `₹${calcColumns(row).totalDeductions.toFixed(2)}`,
        },
        {
            field: "tds",
            headerName: "TDS",
            width: 80,
            renderCell: ({ row }) => `₹${calcColumns(row).tds.toFixed(2)}`,
        },
        {
            field: "net_payable",
            headerName: "Net Payable",
            width: 120,
            renderCell: ({ row }) => `₹${calcColumns(row).netPayable.toFixed(2)}`,
        },
        {
            field: "status",
            headerName: "Status",
            width: 100,
            renderCell: ({ row }) => <small>{row.status}</small>,
        },
        {
            field: "commission_invoice",
            headerName: "Commission Invoice",
            width: 170,
            sortable: false,
            renderCell: ({ row }) => (
                <Stack alignItems="flex-start" spacing={0.3}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleDownloadClick(row, "commission")}
                        sx={{ fontSize: "10px", textTransform: "none" }}
                    >
                        {row.moksha_invoice ? "Re-download" : "Commission"}
                    </Button>
                    {row.moksha_invoice?.commission_invoice_number && (
                        <Typography fontSize="9px" color={colors.greenAccent[400]}>
                            {row.moksha_invoice.commission_invoice_number}
                        </Typography>
                    )}
                </Stack>
            ),
        },
        {
            field: "tcs_invoice",
            headerName: "TCS Invoice",
            width: 150,
            sortable: false,
            renderCell: ({ row }) => (
                <Stack alignItems="flex-start" spacing={0.3}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleDownloadClick(row, "tcs")}
                        sx={{ fontSize: "10px", textTransform: "none" }}
                    >
                        {row.moksha_invoice ? "Re-download" : "TCS"}
                    </Button>
                    {row.moksha_invoice?.tcs_invoice_number && (
                        <Typography fontSize="9px" color={colors.greenAccent[400]}>
                            {row.moksha_invoice.tcs_invoice_number}
                        </Typography>
                    )}
                </Stack>
            ),
        },
    ];

    const modalRow = selectedInvoice?.row;
    const isInvoiced = !!modalRow?.moksha_invoice;
    const previewCalc = modalRow
        ? (isInvoiced
            ? savedToCalc(modalRow.moksha_invoice)
            : computeMoksha({
                rackRate: parseFloat(editRackRate) || 0,
                sellRate: parseFloat(editSellRate) || 0,
                isGstRegistered: modalRow.hotel_id?.gst_enabled !== false,
                nights: getTotalNights(modalRow.checkin_date, modalRow.checkout_date),
            }))
        : null;

    const PreviewRow = ({ label, value, bold }) => (
        <Stack direction="row" justifyContent="space-between" py={0.3}>
            <Typography fontSize="13px" color={colors.grey[300]}>{label}</Typography>
            <Typography fontSize="13px" fontWeight={bold ? "bold" : "normal"}>{value}</Typography>
        </Stack>
    );

    return (
        <Box m="20px" mb={15}>
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                m="20px 0"
            >
                <Header title="MOKSHA INVOICES" subtitle="Manage moksha bookings and invoices" />
            </Box>
            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={1}>
                <Stack direction="row" spacing={1} flexGrow={1}>
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Start Date"
                        sx={{ width: 200 }}
                        value={startDate ? moment.unix(startDate).format("YYYY-MM-DD") : ""}
                        onChange={(e) => {
                            if (e.target.value) {
                                setStartDate(moment(e.target.value, "YYYY-MM-DD").startOf("day").unix());
                                if (!endDate) {
                                    setEndDate(moment(e.target.value, "YYYY-MM-DD").add(1, "days").endOf("day").unix());
                                }
                            } else {
                                setStartDate(0);
                                setEndDate(0);
                            }
                        }}
                        inputRef={startInputRef}
                        onClick={() => startInputRef.current.showPicker()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonth sx={{ cursor: "pointer" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="End Date"
                        sx={{ width: 200 }}
                        inputProps={{ min: startDate ? moment.unix(startDate).format("YYYY-MM-DD") : "" }}
                        value={endDate ? moment.unix(endDate).format("YYYY-MM-DD") : ""}
                        onChange={(e) => {
                            if (e.target.value) {
                                setEndDate(moment(e.target.value, "YYYY-MM-DD").endOf("day").unix());
                            } else {
                                setStartDate(0);
                                setEndDate(0);
                            }
                        }}
                        inputRef={endInputRef}
                        onClick={() => endInputRef.current.showPicker()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonth sx={{ cursor: "pointer" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Autocomplete
                        fullWidth
                        size="small"
                        options={optionsList || []}
                        onChange={(e, value) => setHotelId(value?.id ?? "")}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField {...params} label="Filter by hotel" />}
                        sx={{ width: 250 }}
                    />
                    <CsvDownloader filename="MokshaInvoices" datas={generateCsvDownloadData(mokshaList || [])}>
                        <IconButton title="Export as CSV" sx={{ borderRadius: "5px", bgcolor: colors.greenAccent[700] }}>
                            <Download />
                        </IconButton>
                    </CsvDownloader>
                </Stack>
            </Box>
            <Box
                m="8px 0 0 0"
                height="80vh"
                sx={tableStyle(colors)}
            >
                <DataGrid
                    rows={mokshaList || []}
                    columns={columns}
                    getRowId={(row) => row?._id}
                    components={{ Footer: CustomFooter }}
                />
            </Box>
            <Modal open={confirmModal} onClose={() => !downloading && setConfirmModal(false)}>
                <Box sx={modalStyle}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5">
                            {isInvoiced ? "Download" : "Confirm"} {selectedInvoice?.type === "commission" ? "Commission" : "TCS"} Invoice
                        </Typography>
                        <Close onClick={() => !downloading && setConfirmModal(false)} sx={{ cursor: "pointer" }} />
                    </Stack>

                    <Stack mt={1} mb={2}>
                        <Typography fontWeight="bold">
                            {`${modalRow?.title || ""} ${modalRow?.guest_name || ""}`.trim() || "-"}
                        </Typography>
                        <Typography fontSize="13px" color={colors.grey[300]}>
                            {modalRow?.hotel_id?.name || ""}
                            {modalRow ? ` · ${moment.unix(modalRow.checkin_date).format("DD MMM")} - ${moment.unix(modalRow.checkout_date).format("DD MMM YYYY")}` : ""}
                        </Typography>
                        {isInvoiced && (
                            <Typography fontSize="12px" color={colors.greenAccent[400]} mt={0.5}>
                                Invoiced · {modalRow.moksha_invoice.commission_invoice_number} / {modalRow.moksha_invoice.tcs_invoice_number}
                            </Typography>
                        )}
                    </Stack>

                    <Stack direction="row" spacing={1.5} mb={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Rack Rate"
                            value={editRackRate}
                            onChange={(e) => setEditRackRate(e.target.value)}
                            disabled={isInvoiced || downloading}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Special Price"
                            value={editSellRate}
                            onChange={(e) => setEditSellRate(e.target.value)}
                            disabled={isInvoiced || downloading}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </Stack>

                    {previewCalc && (
                        <Box sx={{ bgcolor: colors.primary[500], borderRadius: 1, p: 1.5, mb: 2 }}>
                            <PreviewRow label="Payable to Property" value={`₹${previewCalc.payableToProperty.toFixed(2)}`} />
                            <PreviewRow label={`GST Slab`} value={`${Math.round(previewCalc.gstSlab * 100)}%`} />
                            <PreviewRow label="PHS Commission" value={`₹${previewCalc.phsCommission.toFixed(2)}`} />
                            <PreviewRow label="GST on Commission (18%)" value={`₹${previewCalc.gst.toFixed(2)}`} />
                            <PreviewRow label="TCS" value={`₹${previewCalc.tcs.toFixed(2)}`} />
                            <PreviewRow label="TDS" value={`₹${previewCalc.tds.toFixed(2)}`} />
                            <PreviewRow label="Total Deductions" value={`₹${previewCalc.totalDeductions.toFixed(2)}`} bold />
                            <PreviewRow label="Net Payable" value={`₹${previewCalc.netPayable.toFixed(2)}`} bold />
                        </Box>
                    )}

                    {!isInvoiced && (
                        <Typography fontSize="11px" color={colors.grey[300]} mb={1.5}>
                            Confirming will save this invoice with invoice numbers and update the booking's rack rate & special price. Figures cannot be edited afterwards.
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1} justifyContent="end">
                        <Button variant="text" onClick={() => setConfirmModal(false)} disabled={downloading}>
                            <b>Cancel</b>
                        </Button>
                        <Button variant="contained" sx={{ minWidth: 170 }} onClick={handleConfirmDownload} disabled={downloading}>
                            {downloading ? <CircularProgress size={20} /> : <b>{isInvoiced ? "Download" : "Confirm & Download"}</b>}
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </Box>
    );
};

export default MokshaInvoices;
